// UsersList.js
import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    Typography,
    Paper,
    Alert,
    Snackbar,
    CircularProgress,
    Fab,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Chip,
    IconButton,
    OutlinedInput,
    FormControlLabel,
    Checkbox
} from '@mui/material';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import { collection, getDocs, addDoc, deleteDoc, doc, updateDoc } from 'firebase/firestore';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { db, auth } from '../Configuration';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import DoneIcon from '@mui/icons-material/Done';

const UsersList = () => {
    const [users, setUsers] = useState([]);
    const [openDialog, setOpenDialog] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        password: '',
        type: 'User',
        campusCode: [],
        viewOnlyMaleData: false,
        viewOnlyFemaleData: false
    });
    const [campuses, setCampuses] = useState([]);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success'
    });

    const fetchUsers = async () => {
        try {
            const userSnapshot = await getDocs(collection(db, 'Users'));
            let userList = userSnapshot.docs.map((doc) => ({
                id: doc.id,
                ...doc.data(),
            }));

            setUsers(userList);
            
            if (userList.length === 0) {
                showSnackbar('No users available for your access level.', 'warning');
            }
        } catch (error) {
            console.error('Error fetching users:', error);
            showSnackbar('Error fetching users', 'error');
        }
    };

    const fetchCampuses = async () => {
        try {
            const campusSnapshot = await getDocs(collection(db, 'Campus'));
            let campusList = campusSnapshot.docs.map((doc) => ({
                id: doc.id,
                ...doc.data(),
            }));


            setCampuses(campusList);
        } catch (error) {
            console.error('Error fetching campuses:', error);
            showSnackbar('Error loading campuses', 'error');
        }
    };

    useEffect(() => {
        fetchCampuses();
        fetchUsers();
    }, []);

    const handleOpenDialog = (user = null) => {
        if (user) {
            setEditingUser(user);
            // Handle both array and single value for backward compatibility
            const campusValue = Array.isArray(user.campusCode) 
                ? user.campusCode
                : (user.campusCode ? [user.campusCode] : []);
            setFormData({
                name: user.name || '',
                email: user.email || '',
                password: '', // Don't populate password for editing
                type: user.type || 'User',
                campusCode: campusValue,
                viewOnlyMaleData: !!user.viewOnlyMaleData,
                viewOnlyFemaleData: !!user.viewOnlyFemaleData
            });
        } else {
            setEditingUser(null);
            setFormData({
                name: '',
                email: '',
                password: '',
                type: 'User',
                campusCode: [],
                viewOnlyMaleData: false,
                viewOnlyFemaleData: false
            });
        }
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setEditingUser(null);
        setFormData({
            name: '',
            email: '',
            password: '',
            type: 'User',
            campusCode: [],
            viewOnlyMaleData: false,
            viewOnlyFemaleData: false
        });
    };

    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const validateForm = () => {
        if (!formData.name.trim()) {
            showSnackbar('Name is required', 'error');
            return false;
        }
        if (!formData.email.trim()) {
            showSnackbar('Email is required', 'error');
            return false;
        }
        if (!editingUser && !formData.password.trim()) {
            showSnackbar('Password is required for new users', 'error');
            return false;
        }
        if (formData.campusCode.length === 0) {
            showSnackbar('Please select at least one campus', 'error');
            return false;
        }
        return true;
    };

    const handleSubmit = async () => {
        if (!validateForm()) return;

        setSubmitting(true);
        try {
            if (editingUser) {
                // Update existing user (without password change)
                const userRef = doc(db, 'Users', editingUser.id);
                await updateDoc(userRef, {
                    name: formData.name,
                    email: formData.email,
                    type: formData.type,
                    campusCode: formData.campusCode, // Already an array
                    viewOnlyMaleData: !!formData.viewOnlyMaleData,
                    viewOnlyFemaleData: !!formData.viewOnlyFemaleData,
                    updatedAt: new Date()
                });
                showSnackbar(`User "${formData.name}" updated successfully`, 'success');
            } else {
                // Create new user with Firebase Authentication
                const userCredential = await createUserWithEmailAndPassword(
                    auth, 
                    formData.email, 
                    formData.password
                );

                // Add user data to Firestore
                await addDoc(collection(db, 'Users'), {
                    uid: userCredential.user.uid,
                    name: formData.name,
                    email: formData.email,
                    type: formData.type,
                    campusCode: formData.campusCode, // Already an array
                    viewOnlyMaleData: !!formData.viewOnlyMaleData,
                    viewOnlyFemaleData: !!formData.viewOnlyFemaleData,
                    createdAt: new Date(),
                    updatedAt: new Date()
                });

                showSnackbar(`User "${formData.name}" created successfully`, 'success');
            }
            handleCloseDialog();
            fetchUsers();
        } catch (error) {
            console.error('Error saving user:', error);
            if (error.code === 'auth/email-already-in-use') {
                showSnackbar('Email already exists. Please use a different email.', 'error');
            } else if (error.code === 'auth/weak-password') {
                showSnackbar('Password is too weak. Please use a stronger password.', 'error');
            } else {
                showSnackbar('Error saving user. Please try again.', 'error');
            }
        } finally {
            setSubmitting(false);
        }
    };

    const handleDelete = async (userId) => {
        const user = users.find(u => u.id === userId);
        if (window.confirm(`Are you sure you want to delete user "${user?.name}"? This action cannot be undone.`)) {
            try {
                await deleteDoc(doc(db, 'Users', userId));
                showSnackbar(`User "${user?.name}" deleted successfully`, 'success');
                fetchUsers();
            } catch (error) {
                console.error('Error deleting user:', error);
                showSnackbar('Error deleting user. Please try again.', 'error');
            }
        }
    };

    const showSnackbar = (message, severity = 'success') => {
        setSnackbar({
            open: true,
            message,
            severity
        });
    };

    const handleCloseSnackbar = () => {
        setSnackbar(prev => ({ ...prev, open: false }));
    };

    const columns = [
        { field: 'name', headerName: 'Name', width: 200 },
        { field: 'email', headerName: 'Email', width: 250 },
        { field: 'type', headerName: 'Type', width: 120 },
        { 
            field: 'campusCode', 
            headerName: 'Campus Codes', 
            width: 200,
            renderCell: (params) => (
                <div>
                    {Array.isArray(params.value) ? params.value.join(", ") : params.value}
                </div>
            )
        },
        {
            field: 'permissions',
            headerName: 'Permissions',
            width: 200,
            sortable: false,
            valueGetter: (value, row) => {
                const male = !!row.viewOnlyMaleData;
                const female = !!row.viewOnlyFemaleData;
                if (male) return 'Male data only';
                if (female) return 'Female data only';
                return '—';
            }
        },
        {
            field: 'actions',
            headerName: 'Actions',
            width: 150,
            sortable: false,
            renderCell: (params) => (
                <Box>
                    <IconButton
                        color="primary"
                        onClick={() => handleOpenDialog(params.row)}
                        size="small"
                        sx={{
                            borderRadius: 2,
                            '&:hover': {
                                transform: 'translateY(-1px)',
                                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                            },
                            transition: 'all 0.3s ease',
                        }}
                    >
                        <EditIcon />
                    </IconButton>
                    <IconButton
                        color="error"
                        onClick={() => handleDelete(params.row.id)}
                        size="small"
                        sx={{
                            borderRadius: 2,
                            '&:hover': {
                                transform: 'translateY(-1px)',
                                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                            },
                            transition: 'all 0.3s ease',
                        }}
                    >
                        <DeleteIcon />
                    </IconButton>
                </Box>
            ),
        }
    ];

    return (
        <Box sx={{ p: 3, background: '#f8f9fa', minHeight: '100vh' }}>
            {/* Header Section */}
            <Box sx={{ mb: 4 }}>
                <Typography 
                    variant="h4" 
                    sx={{ 
                        fontWeight: 700, 
                        color: '#2E7D32',
                        mb: 1,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                    }}
                >
                    👥 User Management
                </Typography>
                <Typography variant="body1" sx={{ color: '#666', mb: 2 }}>
                    Manage user accounts, permissions, and campus access
                </Typography>
            </Box>

            {/* Users Data Grid Card */}
            <Paper 
                elevation={2} 
                sx={{ 
                    p: 3, 
                    mb: 3, 
                    borderRadius: 3,
                    background: 'linear-gradient(135deg, #fff 0%, #f8f9fa 100%)',
                    border: '1px solid rgba(76, 175, 80, 0.1)'
                }}
            >
                <Typography 
                    variant="h6" 
                    sx={{ 
                        fontWeight: 600, 
                        color: '#2E7D32',
                        mb: 2,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                    }}
                >
                    📊 User Records
                </Typography>
                
                <DataGrid
                    rows={users}
                    columns={columns}
                    pageSize={10}
                    rowsPerPageOptions={[10, 25, 50]}
                    slots={{ toolbar: GridToolbar }}
                    disableSelectionOnClick
                    getRowId={(row) => row.id}
                    sx={{
                        border: 'none',
                        height: 600,
                        '& .MuiDataGrid-root': {
                            border: 'none',
                        },
                        '& .MuiDataGrid-cell': {
                            borderBottom: '1px solid rgba(76, 175, 80, 0.1)',
                        },
                        '& .MuiDataGrid-columnHeaders': {
                            backgroundColor: '#f8f9fa',
                            borderBottom: '2px solid #4CAF50',
                        },
                    }}
                />
            </Paper>

            {/* Floating Action Button */}
            <Box sx={{ 
                position: 'fixed', 
                bottom: 20, 
                right: 20, 
                zIndex: 1000
            }}>
                <Fab 
                    color="primary" 
                    aria-label="add" 
                    onClick={() => handleOpenDialog()}
                    sx={{
                        backgroundColor: '#4CAF50',
                        color: '#fff',
                        '&:hover': {
                            backgroundColor: '#2E7D32',
                            transform: 'translateY(-2px)',
                            boxShadow: '0 6px 20px rgba(76, 175, 80, 0.3)',
                        },
                        transition: 'all 0.3s ease',
                    }}
                >
                    <AddIcon />
                </Fab>
            </Box>

            {/* Add/Edit User Dialog */}
            <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
                <DialogTitle>
                    {editingUser ? 'Edit User' : 'Add New User'}
                </DialogTitle>
                <DialogContent>
                    <Box display="flex" flexDirection="column" gap={2} mt={1}>
                        <TextField
                            label="Full Name"
                            value={formData.name}
                            onChange={(e) => handleInputChange('name', e.target.value)}
                            fullWidth
                            required
                        />
                        <TextField
                            label="Email"
                            type="email"
                            value={formData.email}
                            onChange={(e) => handleInputChange('email', e.target.value)}
                            fullWidth
                            required
                        />
                        {!editingUser && (
                            <TextField
                                label="Password"
                                type="password"
                                value={formData.password}
                                onChange={(e) => handleInputChange('password', e.target.value)}
                                fullWidth
                                required
                                helperText="Minimum 6 characters"
                            />
                        )}
                        <FormControl fullWidth>
                            <InputLabel>User Type</InputLabel>
                            <Select
                                value={formData.type}
                                onChange={(e) => handleInputChange('type', e.target.value)}
                                label="User Type"
                            >
                                <MenuItem value="Admin">Admin</MenuItem>
                                <MenuItem value="User">User</MenuItem>
                                <MenuItem value="President">President</MenuItem>
                            </Select>
                        </FormControl>
                        <FormControl fullWidth>
                            <InputLabel>Select Campuses</InputLabel>
                            <Select
                                multiple
                                value={formData.campusCode}
                                onChange={(e) => handleInputChange('campusCode', e.target.value)}
                                input={<OutlinedInput label="Select Campuses" />}
                                renderValue={(selected) => (
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                        {selected.map((value) => (
                                            <Chip key={value} label={value} size="small" />
                                        ))}
                                    </Box>
                                )}
                            >
                                {campuses.map((campus) => (
                                    <MenuItem key={campus.id} value={campus.campusCode}>
                                        {campus.campusName} ({campus.campusCode})
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={formData.viewOnlyMaleData}
                                    onChange={(e) => {
                                        const checked = e.target.checked;
                                        setFormData(prev => ({
                                            ...prev,
                                            viewOnlyMaleData: checked,
                                            viewOnlyFemaleData: checked ? false : prev.viewOnlyFemaleData
                                        }));
                                    }}
                                />
                            }
                            label="View only male student data"
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={formData.viewOnlyFemaleData}
                                    onChange={(e) => {
                                        const checked = e.target.checked;
                                        setFormData(prev => ({
                                            ...prev,
                                            viewOnlyFemaleData: checked,
                                            viewOnlyMaleData: checked ? false : prev.viewOnlyMaleData
                                        }));
                                    }}
                                />
                            }
                            label="View only female student data"
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog} disabled={submitting}>Cancel</Button>
                    <Button
                        onClick={handleSubmit}
                        variant="contained"
                        color="primary"
                        disabled={submitting}
                        startIcon={submitting ? <CircularProgress size={20} /> : <DoneIcon />}
                    >
                        {submitting ? (editingUser ? 'Updating...' : 'Creating...') : (editingUser ? 'Update' : 'Create')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar for notifications */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                sx={{ zIndex: 99999 }}
            >
                <Alert
                    onClose={handleCloseSnackbar}
                    severity={snackbar.severity}
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default UsersList;
